/**
 * Dependencies.
 */
import * as L from 'leaflet';
import { icon, LatLngTuple, Map, MapOptions, TileLayerOptions } from 'leaflet';
import * as LFilter from 'leaflet.tilelayer.colorfilter';
import { isNumber } from 'lodash-es';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { LControl, LMap, LMarker, LPopup, LTileLayer } from 'vue2-leaflet';

import { calculateDistanceBetweenTwoPoints } from '@/modules/main/utils/map';

/**
 * Interface `ICoordinates`.
 */
interface ICoordinates {
  latitude: number;
  longitude: number;
  direction?: number;
}

/**
 * Component.
 */
@Component({
  components: {
    LControl,
    LMap,
    LMarker,
    LPopup,
    LTileLayer
  },
  name: 'change-marker-component'
})
export default class extends Vue {
  /**
   * Property `L`.
   */
  L = L;

  /**
   * Property `leafletMap`.
   */
  leafletMap: Map = null;

  /**
   * Property `originalMarkerPoint`.
   */
  originalMarkerPoint = null;

  /**
   * Property `newMarkerPoint`.
   */
  newMarkerPoint = null;

  /**
   * Property `newMarkerPin`.
   */
  newMarkerPin = require('@/assets/marker-new-address.svg');

  /**
   * Property `originalMarkerPin`.
   */
  originalMarkerPin = require('@/assets/marker.svg');

  /**
   * Property `editedMarker`.
   */
  editedMarker: ICoordinates = null;

  /**
   * Property `limitCircle`.
   */
  limitCircle = null;

  /**
   * Property `applyRadius`.
   */
  @Prop({ default: false })
  applyRadius: boolean;

  /**
   * Property `isEditing`.
   */
  @Prop({ default: false })
  isEditing: boolean;

  /**
   * Property `limitRadius` in meters.
   */
  @Prop({ default: null })
  limitRadius: number;

  /**
   * Property `markerDraggable`.
   */
  @Prop({ default: false })
  markerDraggable: boolean;

  /**
   * Property `newMarker`.
   */
  @Prop({ default: null })
  newMarker: ICoordinates;

  /**
   * Property `originalMarker`.
   */
  @Prop({ default: null })
  originalMarker: ICoordinates;

  /**
   * Property `hasTodayAddressRequest`.
   */
  @Prop({ default: false })
  hasTodayAddressRequest: boolean;

  /**
   * Property `orderType`.
   */
  @Prop({ default: null, type: String })
  orderType: string;

  /**
   * Getter `isDelivery`.
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter `originalMarkerCoordinates`.
   */
  get originalMarkerCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.originalMarker || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `newMarkerCoordinates`.
   */
  get newMarkerCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.newMarker || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `editedMarkerCoordinates`.
   */
  get editedMarkerCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.editedMarker || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `options`.
   */
  get options(): MapOptions {
    return {
      attributionControl: false,
      center: [0, 0],
      maxBounds: [
        [-90, -180],
        [90, 180]
      ],
      minZoom: 2,
      zoom: 2
    };
  }

  /**
   * Getter `tileUrl`.
   */
  get tileUrl() {
    return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  }

  /**
   * Getter `tileLayerOptions`.
   */
  get tileLayerOptions(): TileLayerOptions {
    return {
      referrerPolicy: 'strict-origin-when-cross-origin'
    };
  }

  /**
   * Getter `newMarkerIcon`.
   */
  get newMarkerIcon() {
    const iconUrl = this.newMarkerPin;

    return icon({
      iconAnchor: [32, 49],
      iconSize: [64, 64],
      iconUrl
    });
  }

  /**
   * Getter `originalMarkerIcon`.
   */
  get originalMarkerIcon() {
    const iconUrl = this.originalMarkerPin;

    return icon({
      iconAnchor: [32, 49],
      iconSize: [64, 64],
      iconUrl
    });
  }

  /**
   * Getter `hasRadius`
   */
  get hasRadius() {
    return (
      this.applyRadius && this.limitRadius > 0 && !this.hasTodayAddressRequest
    );
  }

  /**
   * Handle map ready.
   */
  handleMapReady(mapObject) {
    this.editedMarker = { ...this.newMarker };
    this.leafletMap = mapObject;

    this.handleMapGrayFilter();

    this.originalMarkerPoint = this.$refs['originalMarkerPoint'];
    this.newMarkerPoint = this.$refs['newMarkerPoint'];

    if (this.newMarkerPoint) {
      const distance = calculateDistanceBetweenTwoPoints({
        marker1: this.editedMarkerCoordinates,
        marker2: this.originalMarkerCoordinates
      });
      const { latitude = null, longitude = null } = this.editedMarker || {};

      this.$emit('update:lat-lng', {
        isForAutomaticApproval: distance < this.limitRadius ? true : false,
        latitude,
        longitude
      });

      this.handleShowLimitTooltip();

      this.handleSetLimitRadius();
      this.handleAutoFit();
    }
  }

  /**
   * Handle map gray filter.
   */
  handleMapGrayFilter() {
    if (!window.hasOwnProperty('LFilter')) {
      window['LFilter'] = LFilter;
    }

    const myFilter = [
      'invert:20%',
      'grayscale:100%',
      'bright:130%',
      'contrast:100%',
      'saturate:0%',
      'sepia:2%'
    ];
    (L.tileLayer as any)
      .colorFilter('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        filter: myFilter,
        id: 'osm.mapnik'
      })
      .addTo(this.leafletMap);
  }

  /**
   * Handle show limit tooltip.
   */
  handleShowLimitTooltip() {
    const distance = calculateDistanceBetweenTwoPoints({
      marker1: this.editedMarkerCoordinates,
      marker2: this.originalMarkerCoordinates
    });

    this.handleHideLimitTooltip();

    if (
      this.isEditing &&
      (this.hasTodayAddressRequest ||
        (this.newMarkerPoint &&
          this.applyRadius &&
          distance > this.limitRadius))
    ) {
      this.newMarkerPoint.mapObject
        .bindTooltip(this.i18n('TRACKING.LABELS.REQUIRES_CONFIRMATION'), {
          direction: 'top',
          offset: [0, -40]
        })
        .openTooltip();
    }
  }

  /**
   * Handle hide limit tooltip.
   */
  handleHideLimitTooltip() {
    if (this.newMarkerPoint) {
      this.newMarkerPoint.mapObject.unbindTooltip();
    }
  }

  /**
   * Handle set limit radius.
   */
  handleSetLimitRadius() {
    if (
      !this.hasTodayAddressRequest &&
      this.originalMarkerCoordinates &&
      this.applyRadius &&
      this.limitRadius
    ) {
      this.limitCircle = L.circle(this.originalMarkerCoordinates, {
        radius: this.limitRadius
      });

      this.limitCircle.addTo(this.leafletMap);
    }
  }

  /**
   * Handle new marker update.
   */
  handleNewMarkerUpdate({ lat: latitude = null, lng: longitude = null }) {
    this.editedMarker = { latitude, longitude };

    const distance = calculateDistanceBetweenTwoPoints({
      marker1: this.editedMarkerCoordinates,
      marker2: this.originalMarkerCoordinates
    });

    this.newMarkerPoint.mapObject.unbindTooltip();
    if (
      this.hasTodayAddressRequest ||
      (this.applyRadius && distance > this.limitRadius)
    ) {
      this.newMarkerPoint.mapObject
        .bindTooltip(this.i18n('TRACKING.LABELS.REQUIRES_CONFIRMATION'), {
          direction: 'top',
          offset: [0, -40]
        })
        .openTooltip();
    }

    this.$emit('update:lat-lng', {
      distance,
      isForAutomaticApproval: distance < this.limitRadius ? true : false,
      latitude,
      longitude
    });
  }

  /**
   * Handle auto fit coordinates.
   */
  handleAutoFit() {
    if (this.leafletMap) {
      // Markers.
      const latlngs: LatLngTuple[] = [];

      if (this.editedMarkerCoordinates) {
        latlngs.push(this.editedMarkerCoordinates);
      }

      if (this.originalMarkerCoordinates) {
        latlngs.push(this.originalMarkerCoordinates);
      }

      // Circle limit.
      if (
        this.limitCircle &&
        !this.hasTodayAddressRequest &&
        this.applyRadius &&
        this.limitRadius
      ) {
        const circleBounds = this.limitCircle.getBounds();

        const { _northEast: northEast, _southWest: southWest } =
          circleBounds || {};
        const { lat: northEastLat, lng: northEastLng } = northEast || {};
        const { lat: southWestLat, lng: southWestLng } = southWest || {};

        if (northEastLat && northEastLng && southWestLat && southWest) {
          latlngs.push([northEastLat, northEastLng]);
          latlngs.push([southWestLat, southWestLng]);
        }
      }

      // Fit bounds.
      this.leafletMap.fitBounds(latlngs, { padding: [64, 64] });
    }
  }

  /**
   * Handle change is editing.
   */
  @Watch('isEditing')
  handleChangeIsEditing(isEditing) {
    if (this.leafletMap) {
      setTimeout(
        () => {
          this.leafletMap.invalidateSize();
          this.handleAutoFit();

          if (isEditing) {
            this.handleShowLimitTooltip();
          } else {
            this.handleHideLimitTooltip();
          }
        },
        isEditing ? 50 : 500
      );
    }
  }
}
